import { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import useNewBillsAPI from "../../../api/billsAPI";

function toUtcDateString(date) {
  if (!date) return "";

  const utcDate = new Date(
    Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate()),
  );
  return utcDate.toISOString().substring(0, 10);
}

export default function useBillTransactionHistory() {
  const utilityId = useSelector((state) => state?.billsPage?.UtilityID);
  const { pageTitle, TransactionDescription, TransactionImage } = useSelector(
    (state) => state?.billsPage,
  );
  const {
    GetBillTransactionHistory,
    TransactionBills,
    totalCount,
    totalNbOfPage,
    billsLoading,
    IsAutomatable,
  } = useNewBillsAPI();

  const [searchQuery, setSearchQuery] = useState("");
  const [selectedPage, setSelectedPage] = useState(1);
  const [error, setError] = useState("");
  const [dateRange, setDateRange] = useState([
    new Date(new Date().setMonth(new Date().getMonth() - 1)),
    new Date(),
  ]);

  const dateString = useMemo(
    () => ({
      startDateString: toUtcDateString(dateRange?.[0]),
      endDateString: toUtcDateString(dateRange?.[1]),
    }),
    [dateRange],
  );

  const fetchHistory = useCallback(
    async (page = 1, startDate = dateString.startDateString, endDate = dateString.endDateString) => {
      if (!utilityId || !startDate || !endDate) return;

      setError("");

      try {
        await GetBillTransactionHistory(page, startDate, endDate, utilityId);
      } catch (requestError) {
        setError("Unable to load transaction history right now. Please try again.");
      }
    },
    [
      dateString.endDateString,
      dateString.startDateString,
      utilityId,
    ],
  );

  useEffect(() => {
    if (!utilityId || !dateString.startDateString || !dateString.endDateString) {
      return;
    }

    setSelectedPage(1);

    const timer = window.setTimeout(() => {
      fetchHistory(1, dateString.startDateString, dateString.endDateString);
    }, 1000);

    return () => {
      window.clearTimeout(timer);
    };
  }, [
    dateString.endDateString,
    dateString.startDateString,
    fetchHistory,
    utilityId,
  ]);

  const filteredTransactions = useMemo(() => {
    const normalizedQuery = String(searchQuery || "").trim().toLowerCase();

    if (!normalizedQuery) {
      return TransactionBills || [];
    }

    return (TransactionBills || []).filter((transaction) =>
      Object.values(transaction || {}).some((value) =>
        String(value || "").toLowerCase().includes(normalizedQuery),
      ),
    );
  }, [TransactionBills, searchQuery]);

  return {
    billsLoading,
    dateRange,
    dateString,
    error,
    fetchHistory,
    filteredTransactions,
    isAutomatable: IsAutomatable,
    provider: {
      description: TransactionDescription,
      image: TransactionImage,
      title: pageTitle || "Bills",
    },
    searchQuery,
    selectedPage,
    setDateRange,
    setSearchQuery,
    setSelectedPage,
    totalCount,
    totalPages: totalNbOfPage,
    utilityId,
  };
}
